import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { colorOf } from '@/components/ScoreRing';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { Lightbulb, Moon, Timer, Scale, Footprints, Dumbbell, Heart, TrendingDown, TrendingUp, Sunrise, Utensils, Ruler } from 'lucide-react';
import { format } from 'date-fns';

const RANGES = [{ d: 7, l: '7D' }, { d: 30, l: '30D' }, { d: 90, l: '90D' }];

export function CoachDashboard({ clientId }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState(30);

  useEffect(() => { api.get(`/clients/${clientId}/coach-dashboard`).then(r => setData(r.data)); }, [clientId]);
  if (!data) return <div className="text-muted-foreground text-sm">Loading coach dashboard…</div>;

  const { weight, sleep, fasting, recovery_compliance, timeline, insight, steps_today, measurements, training_compliance, movement, nutrition_calories, nutrition_macros, additional_activities } = data;
  const cutoff = format(new Date(Date.now() - range * 864e5), 'yyyy-MM-dd');
  const series = (weight.series || []).filter(p => p.date >= cutoff);
  const toneColor = insight.tone === 'warning' ? '#FFB800' : insight.tone === 'neutral' ? '#8A94A6' : '#65A30D';

  return (
    <div className="space-y-4" data-testid="coach-dashboard">
      {/* Coach Insight */}
      <div className="regen-card-elevated p-5 relative overflow-hidden" data-testid="coach-insight-admin">
        <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full blur-2xl" style={{ background: `${toneColor}1a` }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4" style={{ color: toneColor }} />
            <span className="font-display font-bold" style={{ color: toneColor }}>Coach Recommendation</span>
            <span className="pill text-[10px] ml-auto" style={{ background: `${toneColor}1a`, color: toneColor }}>{insight.tone === 'warning' ? 'Needs attention' : insight.tone === 'neutral' ? 'Not enough data' : 'On track'}</span>
          </div>
          <p className="text-sm leading-relaxed text-foreground/90">{insight.summary}</p>
          {insight.highlights?.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-4">
              {insight.highlights.map(h => (
                <div key={h.label} className="rounded-xl bg-[var(--c-film)] p-2.5 text-center">
                  <div className="font-mono text-base">{h.value}</div>
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{h.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Weight */}
      <div className="regen-card-elevated p-5" data-testid="cd-weight">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Scale className="w-4 h-4 text-[#00C8FF]" /><span className="font-display font-bold">Weight</span></div>
          <div className="flex gap-1">
            {RANGES.map(r => (
              <button key={r.d} onClick={() => setRange(r.d)} data-testid={`cd-range-${r.d}`}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition ${range === r.d ? 'bg-[#00C8FF] text-[#081726]' : 'bg-[var(--c-film)] text-muted-foreground'}`}>{r.l}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-5 gap-2 mb-3">
          <Mini label="Start" value={`${weight.start ?? '—'}`} unit="kg" />
          <Mini label="Current" value={`${weight.current ?? '—'}`} unit="kg" color="#00C8FF" />
          <Mini label="Goal" value={`${weight.goal ?? '—'}`} unit="kg" />
          <Mini label="Change" value={weight.change == null ? '—' : `${weight.change > 0 ? '+' : ''}${weight.change}`} unit="kg" color='#00C8FF' />
          <Mini label="Progress" value={`${weight.progress}`} unit="%" color='#65A30D' />
        </div>
        <div className="h-36">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 6, right: 6, left: -20, bottom: 0 }}>
              <defs><linearGradient id="cdw" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00C8FF" stopOpacity={0.35} /><stop offset="100%" stopColor="#00C8FF" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis domain={['dataMin - 1', 'dataMax + 1']} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} width={28} />
              <Tooltip contentStyle={{ background: '#101E30', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, fontSize: 12 }} formatter={v => [`${v} kg`, 'Weight']} />
              <Area type="monotone" dataKey="weight" stroke="#00C8FF" strokeWidth={2} fill="url(#cdw)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs">
          {weight.weekly_change == null ? null : weight.weekly_change <= 0 ? <TrendingDown className="w-3.5 h-3.5 text-muted-foreground" /> : <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />}
          <span className="text-muted-foreground">Weekly change:</span>
          <span className="font-mono" style={{ color: weight.weekly_change == null ? 'var(--muted-foreground)' : '#00C8FF' }}>{weight.weekly_change == null ? '— this week' : `${weight.weekly_change > 0 ? '+' : ''}${weight.weekly_change} kg this week`}</span>
        </div>
      </div>

      {/* Measurements (Waist) */}
      <div className="regen-card-elevated p-5" data-testid="cd-measurements">
        <div className="flex items-center gap-2 mb-3"><Ruler className="w-4 h-4 text-[#00C8FF]" /><span className="font-display font-bold">Measurements — Waist</span></div>
        {measurements?.waist_current ? (
          <div className="grid grid-cols-3 gap-3 text-center">
            <Mini label="Starting" value={`${measurements.waist_start ?? '—'}`} unit="cm" />
            <Mini label="Current" value={`${measurements.waist_current}`} unit="cm" color="#00C8FF" />
            <Mini label="Lost" value={`-${measurements.waist_lost}`} unit="cm" color='#65A30D' />
          </div>
        ) : <Empty label="No waist measurements logged" />}
        {measurements?.waist_series?.length > 1 && (
          <div className="h-28 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={measurements.waist_series} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                <defs><linearGradient id="cdwaist" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00C8FF" stopOpacity={0.35} /><stop offset="100%" stopColor="#00C8FF" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis domain={['dataMin - 2', 'dataMax + 2']} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} width={26} />
                <Tooltip contentStyle={{ background: '#101E30', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, fontSize: 12 }} formatter={v => [`${v} cm`, 'Waist']} />
                <Area type="monotone" dataKey="waist" stroke="#00C8FF" strokeWidth={2} fill="url(#cdwaist)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Sleep + Fasting */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="regen-card-elevated p-5" data-testid="cd-sleep">
          <div className="flex items-center gap-2 mb-3"><Moon className="w-4 h-4 text-[#A78BFA]" /><span className="font-display font-bold">Sleep</span></div>
          {sleep.last ? (
            <div className="grid grid-cols-2 gap-y-2.5 gap-x-4 text-sm">
              <KV label="Bedtime" value={fmtClock(sleep.last.bed_time)} icon={Moon} />
              <KV label="Wake" value={fmtClock(sleep.last.wake_time)} icon={Sunrise} />
              <KV label="Duration" value={sleep.last.time_in_bed} color={colorOf(sleep.last.duration_color)} />
              <KV label="Quality" value={`${sleep.last.quality}/10`} />
              <KV label="Sleep Score" value={sleep.last.sleep_score} color='#65A30D' />
            </div>
          ) : <Empty label="No sleep logged" />}
        </div>
        <div className="regen-card-elevated p-5" data-testid="cd-fasting">
          <div className="flex items-center gap-2 mb-3"><Timer className="w-4 h-4 text-[#00C8FF]" /><span className="font-display font-bold">Fasting & Meal Timing</span></div>
          {fasting.last ? (
            <div className="grid grid-cols-2 gap-y-2.5 gap-x-4 text-sm">
              <KV label="First Meal" value={fasting.last.first_meal_clock} icon={Sunrise} />
              <KV label="Last Meal" value={fasting.last.last_meal_clock} icon={Utensils} color={colorOf(fasting.last.meal_timing_color)} />
              <KV label="Eating Window" value={fasting.last.eating_window} />
              <KV label="Fasting" value={fasting.last.fasting_duration} color={colorOf(fasting.last.fasting_color)} />
              <KV label="Fasting Score" value={fasting.last.fasting_score} color='#65A30D' />
              <KV label="Meal Timing" value={fasting.last.meal_timing_label} color={colorOf(fasting.last.meal_timing_color)} />
            </div>
          ) : <Empty label="No fasting logged" />}
        </div>
      </div>

      {/* 7-Day Averages */}
      <div className="regen-card-elevated p-5" data-testid="cd-averages">
        <div className="label-mini mb-3">7-Day Averages</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Mini label="Bedtime" value={sleep.avg?.bedtime || '—'} small />
          <Mini label="Wake" value={sleep.avg?.wake || '—'} small />
          <Mini label="Sleep" value={sleep.avg?.duration || '—'} small />
          <Mini label="Quality" value={sleep.avg ? `${sleep.avg.quality}/10` : '—'} small />
          <Mini label="First Meal" value={fasting.avg?.first_meal || '—'} small />
          <Mini label="Last Meal" value={fasting.avg?.last_meal || '—'} small color="#FFB800" />
          <Mini label="Eating Win" value={fasting.avg?.eating_window || '—'} small />
          <Mini label="Fasting" value={fasting.avg?.fasting || '—'} small color="#00C8FF" />
        </div>
      </div>

      {/* Training & Daily Movement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="regen-card-elevated p-5" data-testid="cd-training">
          <div className="flex items-center gap-2 mb-3"><Dumbbell className="w-4 h-4 text-[#00C8FF]" /><span className="font-display font-bold">Training Compliance</span></div>
          <div className="grid grid-cols-2 gap-3 text-center">
            <Mini label="Strength" value={`${training_compliance?.strength_done ?? 0}/${training_compliance?.strength_total ?? 0}`} color="#00C8FF" />
            <Mini label="Cardio / Walking" value={`${training_compliance?.walking_done ?? 0}/${training_compliance?.walking_total ?? 0}`} color='#65A30D' />
          </div>
        </div>
        <div className="regen-card-elevated p-5" data-testid="cd-movement">
          <div className="flex items-center gap-2 mb-3"><Footprints className="w-4 h-4 text-[var(--c-green-ink)]" /><span className="font-display font-bold">Daily Movement</span><span className="text-[10px] text-muted-foreground ml-auto">informational</span></div>
          <div className="grid grid-cols-2 gap-3 text-center">
            <Mini label="Today" value={(movement?.today ?? 0).toLocaleString()} small />
            <Mini label="7-Day Avg" value={(movement?.avg_7d ?? 0).toLocaleString()} small color='#65A30D' />
          </div>
        </div>
      </div>

      {/* Nutrition (calories) + Additional Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="regen-card-elevated p-5" data-testid="cd-nutrition-calories">
          <div className="flex items-center gap-2 mb-3"><Utensils className="w-4 h-4 text-[#FFB800]" /><span className="font-display font-bold">Nutrition (Calories)</span>
            {nutrition_calories?.source && <span className="pill text-[10px] ml-auto bg-[var(--c-film)] text-muted-foreground">{nutrition_calories.source === 'manual' ? 'Coach target' : 'From meal plan'}</span>}
          </div>
          {nutrition_calories?.planned > 0 ? (
            <>
              <div className="grid grid-cols-3 gap-3 text-center">
                <Mini label="Planned" value={nutrition_calories.planned} unit="kcal" />
                <Mini label="Additional" value={`+${nutrition_calories.additional}`} unit="kcal" color="#FFB800" />
                <Mini label="Actual" value={nutrition_calories.actual} unit="kcal" color="#00C8FF" />
              </div>
              {nutrition_calories.compliance != null && (
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Today's nutrition compliance:</span>
                  <span className="font-mono font-bold" style={{ color: nutrition_calories.compliance >= 85 ? '#65A30D' : nutrition_calories.compliance >= 60 ? '#FFB800' : '#FF5C5C' }}>{nutrition_calories.compliance}%</span>
                </div>
              )}
              {nutrition_macros?.target && (
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {['calories', 'protein', 'carbs', 'fat'].map(k => {
                    const fl = (nutrition_macros.flags || {})[k] || 'none';
                    const col = { green: '#65A30D', orange: '#FFB800', red: '#FF5C5C', none: '#5B6B7E' }[fl];
                    return (
                      <div key={k} className="rounded-lg bg-[var(--c-film)] p-2 text-center" data-testid={`cd-macro-${k}`}>
                        <div className="label-mini capitalize">{k === 'calories' ? 'Cal' : k}</div>
                        <div className="font-mono text-xs mt-0.5">
                          <span style={{ color: col }}>{Math.round((nutrition_macros.logged || {})[k] || 0)}</span>
                          <span className="text-muted-foreground">/{Math.round(nutrition_macros.target[k] || 0)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : <Empty label="No calorie target set" />}
        </div>
        <div className="regen-card-elevated p-5" data-testid="cd-additional-activities">
          <div className="flex items-center gap-2 mb-3"><Dumbbell className="w-4 h-4 text-[var(--c-green-ink)]" /><span className="font-display font-bold">Additional Activities</span><span className="text-[10px] text-muted-foreground ml-auto">bonus · 7d</span></div>
          <div className="grid grid-cols-2 gap-3 text-center mb-3">
            <Mini label="Sessions" value={`+${additional_activities?.sessions ?? 0}`} color='#65A30D' />
            <Mini label="Total Time" value={`+${additional_activities?.total_minutes ?? 0}`} unit="min" color='#65A30D' />
          </div>
          {additional_activities?.by_activity?.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {additional_activities.by_activity.map(a => (
                <span key={a.name} className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)] text-[11px]">{a.name} · {a.minutes}min</span>
              ))}
            </div>
          ) : <div className="text-xs text-muted-foreground text-center">No extra activities logged</div>}
        </div>
      </div>

      {/* Recovery Activity Compliance */}
      <div className="regen-card-elevated p-5" data-testid="cd-compliance">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Heart className="w-4 h-4 text-[var(--c-green-ink)]" /><span className="font-display font-bold">Recovery Activities</span></div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Footprints className="w-3.5 h-3.5 text-[var(--c-green-ink)]" /> {steps_today.toLocaleString()} steps today</div>
        </div>
        {recovery_compliance.length === 0 ? <Empty label="No assigned recovery activities this week" /> : (
          <div className="space-y-2.5">
            {recovery_compliance.map((a, idx) => {
              const isSteps = a.metric === 'steps';
              const done = isSteps ? a.done_steps : a.completed;
              const target = isSteps ? a.target_steps : a.assigned;
              const pct = target ? Math.min(100, Math.round((done / target) * 100)) : 0;
              return (
                <div key={`${a.name}-${idx}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>{a.name}</span>
                    <span className="font-mono text-muted-foreground">{isSteps ? `${done.toLocaleString()} / ${target.toLocaleString()} steps` : `${done} / ${target} completed`}</span>
                  </div>
                  <div className="h-1.5 bg-[var(--c-film)] rounded-full overflow-hidden"><div className="h-full rounded-full bg-[var(--c-green)]" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Coach Timeline */}
      <div className="regen-card-elevated p-5" data-testid="cd-timeline">
        <div className="label-mini mb-3">Coach Timeline</div>
        {timeline.length === 0 ? <Empty label="No logged data yet" /> : (
          <div className="space-y-4">
            {timeline.map(t => (
              <div key={t.date} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#00C8FF] mt-1.5" />
                  <div className="flex-1 w-px bg-[var(--c-film)] my-1" />
                </div>
                <div className="flex-1 pb-1">
                  <div className="text-sm font-semibold">{format(new Date(t.date), 'EEE, MMM d')}</div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
                    {t.weight != null && <span className="flex items-center gap-1"><Scale className="w-3 h-3 text-[#00C8FF]" />{t.weight} kg</span>}
                    {t.sleep && <span className="flex items-center gap-1"><Moon className="w-3 h-3 text-[#A78BFA]" />{t.sleep}{t.sleep_quality ? ` · Q${t.sleep_quality}` : ''}</span>}
                    {t.fasting && <span className="flex items-center gap-1"><Timer className="w-3 h-3 text-[#00C8FF]" />Fasted {t.fasting}</span>}
                    {t.last_meal && <span className="flex items-center gap-1"><Utensils className="w-3 h-3 text-[#FFB800]" />Last {t.last_meal}</span>}
                    {t.training_done && <span className="flex items-center gap-1 text-[var(--c-green-ink)]"><Dumbbell className="w-3 h-3" />Training ✓</span>}
                    {t.recovery_done?.map((r, ri) => <span key={`${t.date}-rec-${ri}`} className="flex items-center gap-1 text-[var(--c-green-ink)]"><Heart className="w-3 h-3" />{r} ✓</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function fmtClock(hm) {
  if (!hm) return '—';
  const [h, m] = hm.split(':').map(Number);
  const sfx = h < 12 ? 'AM' : 'PM'; const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, '0')} ${sfx}`;
}
function Mini({ label, value, unit, color, small }) {
  return (
    <div>
      <div className="label-mini">{label}</div>
      <div className={`font-mono mt-1 ${small ? 'text-base' : 'text-xl'}`} style={color ? { color } : {}}>{value}{unit && <span className="text-xs text-muted-foreground ml-0.5">{unit}</span>}</div>
    </div>
  );
}
function KV({ label, value, color, icon: Icon }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</span>
      <span className="font-mono font-semibold" style={color ? { color } : {}}>{value}</span>
    </div>
  );
}
function Empty({ label }) { return <div className="text-center text-sm text-muted-foreground py-4">{label}</div>; }
